/**
 * The Academic Bio Generator: one set of facts, four bios — a conference speaker bio, a
 * journal author note, a university profile page and a social media profile.
 *
 * Length is a budget, not a promise. The identity sentence and at least one research
 * interest always appear; then achievements, publications and the personal line are
 * added, in that order, with as many items as fit within the target plus a tenth.
 * A bio built from short answers comes out shorter than the target, and the live word
 * count says so rather than padding it.
 *
 * Third-person sentences are written around possessives ("Her research…", "Okafor's
 * achievements…") rather than a pronoun subject, so no frame needs to agree a verb with
 * "she" or "they".
 */

import { graphemeLength } from "@/lib/graphemes";
import { t } from "@/lib/i18n/interpolate";
import {
  asFragment,
  asSentence,
  cleanLine,
  countWords,
  fillFrame,
  joinList,
  orPlaceholder,
  splitItems,
  toPlain,
  withDefiniteArticle,
  withIndefiniteArticle,
} from "@/lib/tool-kit/correspondence/compose";

export type BioPerson = "third" | "first";
export type BioPronoun = "name" | "she" | "he" | "they";
export const BIO_LENGTHS = ["50", "100", "150"] as const;
export type BioLength = (typeof BIO_LENGTHS)[number];
export const BIO_CONTEXTS = [
  "conference",
  "journal",
  "profile",
  "social",
] as const;
export type BioContext = (typeof BIO_CONTEXTS)[number];

/** Journals print short notes; a 150-word author note is rarely wanted. */
const JOURNAL_MAX_WORDS = 100;
/** The shortest common profile fields hold about 150–160 characters. */
export const SOCIAL_MAX_CHARACTERS = 160;
const SOCIAL_SEPARATOR = " · ";
const SOCIAL_LIST_SEPARATOR = ", ";

export const ACADEMIC_BIO_BANK = {
  possessives: {
    she: "Her",
    he: "His",
    they: "Their",
  },
  possessiveName: "{name}'s",
  possessiveFirst: "My",
  pronounLabels: {
    she: "she/her",
    he: "he/him",
    they: "they/them",
  },
  identityThird: "{name} is {position} at {institution}.",
  identityFirst: "I am {position} at {institution}.",
  interests: [
    "?field:{possessive} research in {field} focuses on {interests}.",
    "!field:{possessive} research focuses on {interests}.",
  ],
  achievementsThird: "{possessive} achievements include {achievements}.",
  achievementsFirst: "Highlights of my work include {achievements}.",
  publications: "Selected publications include {publications}.",
  bioPlaceholders: {
    name: "Your name",
    position: "Your position",
    institution: "Institution",
    interests: "your research interests",
  },
} as const;

export interface AcademicBioBank {
  possessives: Readonly<Record<Exclude<BioPronoun, "name">, string>>;
  possessiveName: string;
  possessiveFirst: string;
  pronounLabels: Readonly<Record<Exclude<BioPronoun, "name">, string>>;
  identityThird: string;
  identityFirst: string;
  interests: readonly string[];
  achievementsThird: string;
  achievementsFirst: string;
  publications: string;
  bioPlaceholders: Readonly<Record<string, string>>;
}

export interface AcademicBioInput {
  name: string;
  /** How a third-person bio refers back to you when there are no pronouns. */
  shortName: string;
  pronoun: BioPronoun;
  position: string;
  institution: string;
  field: string;
  /** Separated by commas, semicolons or new lines. */
  interests: string;
  /** One per line. */
  achievements: string;
  /** One per line. */
  publications: string;
  /** One sentence, in the same person as the bio. */
  personal: string;
  length: BioLength;
  person: BioPerson;
}

export interface BioVersion {
  context: BioContext;
  text: string;
  words: number;
  /** For the social profile, whose limit is in characters. */
  characters: number;
  /** The word target this version was fitted to (0 for the social profile). */
  target: number;
}

interface Facts {
  name: string;
  position: string;
  institution: string;
  field: string;
  interests: string[];
  achievements: string[];
  publications: string[];
  personal: string;
}

function facts(input: AcademicBioInput, bank: AcademicBioBank): Facts {
  const ph = bank.bioPlaceholders;
  const position = asFragment(input.position);
  const interests = splitItems(input.interests, { commas: true });
  return {
    name: orPlaceholder(cleanLine(input.name), ph.name ?? ""),
    position:
      position === ""
        ? orPlaceholder("", ph.position ?? "")
        : withIndefiniteArticle(position),
    institution: orPlaceholder(
      withDefiniteArticle(asFragment(input.institution)),
      ph.institution ?? ""
    ),
    field: asFragment(input.field),
    interests:
      interests.length > 0
        ? interests
        : [orPlaceholder("", ph.interests ?? "")],
    achievements: splitItems(input.achievements),
    publications: splitItems(input.publications),
    personal: asSentence(input.personal),
  };
}

function possessive(
  input: AcademicBioInput,
  person: BioPerson,
  bank: AcademicBioBank,
  name: string
): string {
  if (person === "first") {
    return bank.possessiveFirst;
  }
  if (input.pronoun === "name") {
    const short = cleanLine(input.shortName);
    return t(bank.possessiveName, { name: short === "" ? name : short });
  }
  return bank.possessives[input.pronoun];
}

/** A sentence that can be given fewer list items to save words. */
interface BioPart {
  items: number;
  required: boolean;
  render: (count: number) => string;
}

function textOf(parts: readonly BioPart[], counts: readonly number[]): string {
  return parts
    .map((part, index) =>
      (counts[index] ?? 0) > 0 ? part.render(counts[index] ?? 0) : ""
    )
    .filter((sentence) => sentence !== "")
    .join(" ");
}

/**
 * Fit sentences into a word budget: required parts always (lists trimmed to one item
 * if they must be), then each optional part with as many items as still fit.
 */
export function fitToBudget(parts: readonly BioPart[], limit: number): string {
  const counts = parts.map((part) => (part.required ? part.items : 0));
  for (let index = parts.length - 1; index >= 0; index--) {
    while (
      parts[index].required &&
      (counts[index] ?? 0) > 1 &&
      countWords(textOf(parts, counts)) > limit
    ) {
      counts[index] = (counts[index] ?? 0) - 1;
    }
  }
  for (const [index, part] of parts.entries()) {
    if (part.required) {
      continue;
    }
    for (let count = part.items; count >= 1; count--) {
      counts[index] = count;
      if (countWords(textOf(parts, counts)) <= limit) {
        break;
      }
      counts[index] = 0;
    }
  }
  return textOf(parts, counts);
}

function listPart(
  items: readonly string[],
  required: boolean,
  render: (joined: string) => string,
  locale: string
): BioPart {
  return {
    items: items.length,
    required,
    render: (count) => render(joinList(items.slice(0, count), locale)),
  };
}

function sentencePart(sentence: string): BioPart {
  return {
    items: sentence === "" ? 0 : 1,
    required: false,
    render: () => sentence,
  };
}

function proseBio(
  context: Exclude<BioContext, "social">,
  input: AcademicBioInput,
  bank: AcademicBioBank,
  locale: string
): BioVersion {
  const f = facts(input, bank);
  const person = context === "journal" ? "third" : input.person;
  const values = {
    name: f.name,
    position: f.position,
    institution: f.institution,
    field: f.field,
    possessive: possessive(input, person, bank, f.name),
  };
  const flags = new Set<string>(f.field === "" ? [] : ["field"]);
  const identity = t(
    person === "first" ? bank.identityFirst : bank.identityThird,
    values
  );
  const interestsFrame =
    bank.interests.find((frame) => fillFrame(frame, values, flags) !== null) ??
    "";
  const achievementsFrame =
    person === "first" ? bank.achievementsFirst : bank.achievementsThird;

  const parts: BioPart[] = [
    { items: 1, required: true, render: () => identity },
    listPart(
      f.interests,
      true,
      (interests) =>
        fillFrame(interestsFrame, { ...values, interests }, flags) ?? "",
      locale
    ),
    listPart(
      f.achievements,
      false,
      (achievements) => t(achievementsFrame, { ...values, achievements }),
      locale
    ),
  ];
  if (context === "profile") {
    parts.push(
      listPart(
        f.publications,
        false,
        (publications) => t(bank.publications, { publications }),
        locale
      )
    );
  }
  if (context !== "journal") {
    parts.push(sentencePart(f.personal));
  }

  const chosen = Number(input.length);
  const target =
    context === "journal" ? Math.min(chosen, JOURNAL_MAX_WORDS) : chosen;
  const text = fitToBudget(parts, Math.round(target * 1.1));
  return {
    context,
    text,
    words: countWords(text),
    characters: graphemeLength(toPlain(text), locale),
    target,
  };
}

function socialBio(
  input: AcademicBioInput,
  bank: AcademicBioBank,
  locale: string
): BioVersion {
  const ph = bank.bioPlaceholders;
  const head = [
    orPlaceholder(asFragment(input.position), ph.position ?? ""),
    asFragment(input.institution),
  ]
    .filter((part) => part !== "")
    .join(SOCIAL_LIST_SEPARATOR);
  const interests = splitItems(input.interests, { commas: true });
  const pronouns =
    input.pronoun === "name" ? "" : bank.pronounLabels[input.pronoun];
  const build = (count: number) =>
    [head, interests.slice(0, count).join(SOCIAL_LIST_SEPARATOR), pronouns]
      .filter((part) => part !== "")
      .join(SOCIAL_SEPARATOR);
  let count = interests.length;
  while (
    count > 1 &&
    graphemeLength(toPlain(build(count)), locale) > SOCIAL_MAX_CHARACTERS
  ) {
    count -= 1;
  }
  const text = build(count);
  return {
    context: "social",
    text,
    words: countWords(text),
    characters: graphemeLength(toPlain(text), locale),
    target: 0,
  };
}

/** All four bios, in display order. */
export function buildBios(
  input: AcademicBioInput,
  bank: AcademicBioBank,
  locale: string
): BioVersion[] {
  return [
    proseBio("conference", input, bank, locale),
    proseBio("journal", input, bank, locale),
    proseBio("profile", input, bank, locale),
    socialBio(input, bank, locale),
  ];
}
